import { action, mutator } from 'satcheljs';
import { getStore } from '../store/store';
import type { DrawerPaneInfo } from '../store/schema/DrawerPaneState';
import getTabIdFromWindow from '../utils/getTabIdFromWindow';
import { DEFAULT_DRAWER_PANE_WIDTH } from '../constants/constants';

export interface DrawerPaneShownChangedActionMessage {
    paneId: string;
    isShown: boolean;
    targetWindow?: Window;
}

export const drawerPaneShownChanged: (
    paneId: string,
    isShown: boolean,
    targetWindow?: Window
) => DrawerPaneShownChangedActionMessage = action(
    'drawerPaneShownChanged',
    (
        paneId: string,
        isShown: boolean,
        targetWindow?: Window
    ): DrawerPaneShownChangedActionMessage => ({
        paneId,
        isShown,
        targetWindow,
    })
);

mutator(drawerPaneShownChanged, actionMessage => {
    const tabId = getTabIdFromWindow(actionMessage.targetWindow);
    const tabMap = getOrCreateTabMap(tabId);

    if (actionMessage.isShown) {
        // Close any previously shown pane in this window
        for (const state of tabMap.values()) {
            if (state.isShown) {
                state.isShown = false;
            }
        }
    }

    // Update or create the pane entry
    const existing = tabMap.get(actionMessage.paneId);
    if (existing) {
        existing.isShown = actionMessage.isShown;
    } else {
        const newState: DrawerPaneInfo = {
            isShown: actionMessage.isShown,
            currentWidth: DEFAULT_DRAWER_PANE_WIDTH,
        };
        tabMap.set(actionMessage.paneId, newState);
    }
});

/**
 * Returns the inner pane map for the given tabId, creating it if it doesn't exist.
 */
function getOrCreateTabMap(tabId: string): Map<string, DrawerPaneInfo> {
    const store = getStore();
    if (!store.paneStateMap.has(tabId)) {
        store.paneStateMap.set(tabId, new Map<string, DrawerPaneInfo>());
    }
    return store.paneStateMap.get(tabId) ?? new Map<string, DrawerPaneInfo>();
}
